import { useQuery, UseQueryResult } from "@tanstack/react-query";

import {
  ISystemCollection,
  ISystemResponse,
  ISystemRole,
  ISystemSubtype,
  ISystemTag,
  ISystemTemplate,
  ISystemUser,
  ISystemVertical,
} from "@dl/declarations";
import api from "./api";
import { ALLOWED_LIMITS, QUERY_KEYS } from "./constants";

/** Lookup queries */

type CommonOptions = {
  enabled?: boolean;
  fetchOnce?: boolean;
};

export const rolesLookupsQuery = (
  extraQueryParams: Record<string, string> = {},
  options: CommonOptions = {},
): UseQueryResult<ISystemRole[], Error> => {
  const { fetchOnce = true, ...restOfOptions } = options;

  const queryKey = [QUERY_KEYS.ROLES_LOOKUPS];

  if (!fetchOnce) {
    queryKey.push(JSON.stringify(extraQueryParams));
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: ALLOWED_LIMITS["100"],
        ...extraQueryParams,
      });
      const res = await api.get<ISystemResponse<ISystemRole[]>>("/roles", {
        params,
      });
      return res.data.data;
    },
    refetchOnWindowFocus: false,
    ...restOfOptions,
  });
};

export const verticalsLookupsQuery = (
  extraQueryParams: Record<string, string> = {},
  options: CommonOptions = {},
): UseQueryResult<Array<ISystemVertical>, Error> => {
  const { fetchOnce = true, ...restOfOptions } = options;

  const queryKey = [QUERY_KEYS.VERTICALS_LOOKUPS];

  if (!fetchOnce) {
    queryKey.push(JSON.stringify(extraQueryParams));
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: ALLOWED_LIMITS["100"],
        ...extraQueryParams,
      });
      const res = await api.get<ISystemResponse<Array<ISystemVertical>>>(
        "/verticals",
        { params },
      );
      return res.data.data;
    },
    refetchOnWindowFocus: false,
    ...restOfOptions,
  });
};

export const authorsLookupsQuery = (
  extraQueryParams: {
    is_active?: boolean;
    team?: string;
    custom_role?: string;
    allowed_permissions?: Array<string>;
  },
  options: CommonOptions = {},
): UseQueryResult<Array<ISystemUser>, Error> => {
  const { fetchOnce = true, ...restOfOptions } = options;

  const queryKey = [QUERY_KEYS.AUTHORS_LOOKUPS];

  const {
    is_active,
    custom_role = "",
    team = "",
    ...usedExtraQueryParams
  } = extraQueryParams;

  if (!fetchOnce) {
    queryKey.push(JSON.stringify(usedExtraQueryParams));
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: ALLOWED_LIMITS["100"],
        // ...extraQueryParams,
      });
      const res = await api.get<ISystemResponse<Array<ISystemUser>>>("/users", {
        params,
      });
      return res.data.data;
    },
    select(data) {
      let used_data = [...data];

      if (is_active) {
        used_data = used_data.filter((user) => user.is_active);
      }

      if (team) {
        used_data = used_data.filter(
          (user) =>
            user.team && (team as string).split(",").includes(user.team),
        );
      }

      if (custom_role) {
        used_data = used_data.filter(
          (user) =>
            user.customRoleObject &&
            (custom_role as string)
              .split(",")
              .includes(user.customRoleObject.slug),
        );
      }

      return used_data;
    },
    refetchOnWindowFocus: false,
    ...restOfOptions,
  });
};

export const subtypesLookupsQuery = (
  extraQueryParams: Record<string, string> = {},
  options: CommonOptions = {},
): UseQueryResult<Array<ISystemSubtype>, Error> => {
  const { fetchOnce = true, ...restOfOptions } = options;

  const queryKey = [QUERY_KEYS.SUBTYPES_LOOKUPS];

  const { used_for, ...usedExtraQueryParams } = extraQueryParams;

  if (!fetchOnce) {
    queryKey.push(JSON.stringify(usedExtraQueryParams));
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: ALLOWED_LIMITS["100"],
        ...usedExtraQueryParams,
      });
      const res = await api.get<ISystemResponse<Array<ISystemSubtype>>>(
        "/subtypes",
        { params },
      );
      return res.data.data;
    },
    refetchOnWindowFocus: false,
    select(data) {
      if (used_for) {
        return data.filter((subtype) => subtype.used_for.includes(used_for));
      }

      return data;
    },
    ...restOfOptions,
  });
};

export const collectionsLookupsQuery = (
  extraQueryParams?: Record<string, string>,
  options: CommonOptions = {},
): UseQueryResult<Array<ISystemCollection>, Error> => {
  const { fetchOnce, ...restOfOptions } = options;

  const queryKey = ["collectionsLookupsQuery"];

  if (!fetchOnce) {
    queryKey.push(JSON.stringify(extraQueryParams));
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: ALLOWED_LIMITS["100"],
        ...extraQueryParams,
      });
      const res = await api.get<ISystemResponse<Array<ISystemCollection>>>(
        "/collections",
        { params },
      );
      return res.data.data;
    },
    refetchOnWindowFocus: false,
    ...restOfOptions,
  });
};

export const templatesLookupsQuery = (
  extraQueryParams?: Record<string, string>,
  options: CommonOptions = {},
): UseQueryResult<Array<ISystemTemplate>, Error> => {
  const { fetchOnce, ...restOfOptions } = options;

  const queryKey = ["templatesLookupsQuery"];

  if (!fetchOnce) {
    queryKey.push(JSON.stringify(extraQueryParams));
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: ALLOWED_LIMITS["100"],
        ...extraQueryParams,
      });
      const res = await api.get<ISystemResponse<Array<ISystemTemplate>>>(
        "/templates",
        { params },
      );
      return res.data.data;
    },
    refetchOnWindowFocus: false,
    ...restOfOptions,
  });
};

export const tagsLookupsQuery = (
  extraQueryParams?: Record<string, string>,
  options: CommonOptions = {},
): UseQueryResult<Array<ISystemTag>, Error> => {
  const { fetchOnce, ...restOfOptions } = options;

  const queryKey = ["tagsLookupsQuery"];

  if (!fetchOnce) {
    queryKey.push(JSON.stringify(extraQueryParams));
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: ALLOWED_LIMITS["100"],
        ...extraQueryParams,
      });
      const res = await api.get<ISystemResponse<Array<ISystemTag>>>("/tags", {
        params,
      });
      return res.data.data;
    },
    refetchOnWindowFocus: false,
    ...restOfOptions,
  });
};

export const tagsSuggestionsLookupsQuery = (
  extraQueryParams?: Record<string, string>,
  options: CommonOptions = {},
): UseQueryResult<Array<ISystemTag>, Error> => {
  const { fetchOnce, ...restOfOptions } = options;

  const queryKey = ["tagsSuggestionsLookupsQuery"];

  if (!fetchOnce) {
    queryKey.push(JSON.stringify(extraQueryParams));
  }

  return useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: ALLOWED_LIMITS["10"],
        ...extraQueryParams,
      });
      const res = await api.get<ISystemResponse<Array<ISystemTag>>>(
        "/tags/suggestions",
        {
          params,
        },
      );
      return res.data.data;
    },
    refetchOnWindowFocus: false,
    ...restOfOptions,
  });
};

export const dummyQuery = (
  extraQueryParams?: Record<string, string>,
  options: CommonOptions = {},
) => {
  return useQuery({
    queryKey: ["dummy", JSON.stringify(extraQueryParams)],
    queryFn: async () => {
      return [];
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const getQuery = (
  key:
    | "verticalsLookupsQuery"
    | "authorsLookupsQuery"
    | "subtypesLookupsQuery"
    | "collectionsLookupsQuery"
    | "tagsLookupsQuery"
    | "templatesLookupsQuery"
    | "none",
) => {
  switch (key) {
    case "verticalsLookupsQuery":
      return verticalsLookupsQuery;
    case "authorsLookupsQuery":
      return authorsLookupsQuery;
    case "subtypesLookupsQuery":
      return subtypesLookupsQuery;
    case "collectionsLookupsQuery":
      return collectionsLookupsQuery;
    case "tagsLookupsQuery":
      return tagsLookupsQuery;
    case "templatesLookupsQuery":
      return templatesLookupsQuery;
    case "none":
    default:
      return dummyQuery;
  }
};

/** Rest queries */
